import React, { useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  CreditCard,
  Monitor,
  ShoppingCart,
  Smartphone,
  Layers,
  MoreHorizontal,
  ShieldCheck,
  Activity,
  Headphones,
  TrendingUp,
  Phone,
  Mail,
  CheckCircle2,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import { toast } from 'sonner';


const API = "/api";

const SOLUTIONS = [
  { value: 'Card Machines',       icon: CreditCard },
  { value: 'EPOS',                icon: Monitor },
  { value: 'eCommerce',           icon: ShoppingCart },
  { value: 'SoftPOS',             icon: Smartphone },
  { value: 'Integrated Payments', icon: Layers },
  { value: 'Other',               icon: MoreHorizontal },
];

const TRUST_BLOCKS = [
  { icon: ShieldCheck, title: 'Secure & Compliant',  body: 'PCI DSS certified infrastructure.' },
  { icon: Activity,    title: 'Flexible Solutions',  body: 'In-store, online and mobile payments.' },
  { icon: Headphones,  title: 'Dedicated Support',   body: 'Real people supporting your business.' },
  { icon: TrendingUp,  title: 'Scalable Technology', body: 'Solutions designed to grow with you.' },
];

const NEXT_STEPS = [
  'Our team reviews your enquiry',
  'We contact you to understand your requirements',
  'We recommend the best solution for your business',
  'Simple onboarding and setup',
];

const Eyebrow = ({ children }) => (
  <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.22em] mb-3">{children}</p>
);

const ContactSales = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    contactName: '',
    businessName: '',
    email: '',
    phone: '',
    solutions: [],
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleSolution = (v) =>
    setForm((f) => ({
      ...f,
      solutions: f.solutions.includes(v)
        ? f.solutions.filter((s) => s !== v)
        : [...f.solutions, v],
    }));

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!form.contactName || !form.email || !form.phone || form.solutions.length === 0) {
    toast.error("Please complete all required fields.");
    return;
  }

  setSubmitting(true);

  try {
    const res = await fetch(`${API}/contact`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        full_name: form.contactName,
        email: form.email,
        business_name: form.businessName || "Not Provided",
        business_type_location: form.solutions.join(", "),
        message: `Phone: ${form.phone}`,
      }),
    });

    if (!res.ok) {
      throw new Error("Request failed");
    }

    toast.success("Thanks! Our sales team will be in touch within 24 hours.");

    setForm({
      contactName: "",
      businessName: "",
      email: "",
      phone: "",
      solutions: [],
    });
  } catch (err) {
    toast.error("Unable to submit your enquiry. Please try again.");
    console.error(err);
  } finally {
    setSubmitting(false);
  }
};

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="contact-sales-page">
      <Header />
      <div className="h-20" />

      {/* ╔═════════════════════ HERO + FORM ═════════════════════╗ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#f7f9fc] via-white to-[#eef3f8]" data-testid="cs-hero">
        <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#1F4E79]/10 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-40 -left-32 w-96 h-96 rounded-full bg-[#ef5b25]/10 blur-3xl" aria-hidden="true" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 sm:pt-16 sm:pb-24">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-[#1F4E79] font-medium mb-10 transition-colors duration-200"
            data-testid="cs-back"
          >
            <ArrowLeft size={18} />
            Back
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            {/* Left — copy */}
            <div className="lg:col-span-5 lg:sticky lg:top-24">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-white/80 via-white/95 to-white/80 backdrop-blur-md border border-white/80 text-[#1F4E79] text-[11px] font-bold uppercase tracking-[0.22em] mb-6 shadow-[0_4px_20px_-2px_rgba(31,78,121,0.18)] ring-1 ring-inset ring-white/60">
                <Phone size={13} className="text-[#ef5b25]" strokeWidth={2} />
                Contact Sales
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[56px] font-bold text-[#0a1929] leading-[1.05] tracking-tight mb-6" data-testid="cs-title">
                Let's Build Your<br />
                <span className="text-[#1F4E79]">Payment Solution.</span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed font-medium">
                Speak with our team about payment terminals, eCommerce, EPOS, SoftPOS and integrated acquiring solutions tailored to your business.
              </p>
            </div>

            {/* Right — form */}
            <form onSubmit={handleSubmit} className="lg:col-span-7 bg-white rounded-2xl shadow-2xl border border-gray-100 p-7 sm:p-10" data-testid="cs-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <div data-testid="cs-field-contact">
                  <label className="block text-sm font-bold text-gray-800 mb-2">Contact Name <span className="text-red-500">*</span></label>
                  <input type="text" value={form.contactName} onChange={update('contactName')} className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150" data-testid="cs-input-contact" />
                </div>
                <div data-testid="cs-field-business">
                  <label className="block text-sm font-bold text-gray-800 mb-2">Business Name <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input type="text" value={form.businessName} onChange={update('businessName')} className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150" data-testid="cs-input-business" />
                </div>
                <div data-testid="cs-field-email">
                  <label className="block text-sm font-bold text-gray-800 mb-2">Email <span className="text-red-500">*</span></label>
                  <input type="email" value={form.email} onChange={update('email')} className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150" data-testid="cs-input-email" />
                </div>
                <div data-testid="cs-field-phone">
                  <label className="block text-sm font-bold text-gray-800 mb-2">Phone <span className="text-red-500">*</span></label>
                  <input type="tel" value={form.phone} onChange={update('phone')} className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150" data-testid="cs-input-phone" />
                </div>
              </div>

              <div className="mb-7" data-testid="cs-field-solutions">
                <label className="block text-sm font-bold text-gray-800 mb-1">Which solutions are you interested in? <span className="text-red-500">*</span></label>
                <p className="text-xs text-gray-500 mb-3">Select all that apply.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {SOLUTIONS.map((s, i) => {
                    const Icon = s.icon;
                    const active = form.solutions.includes(s.value);
                    return (
                      <button
                        type="button"
                        key={s.value}
                        onClick={() => toggleSolution(s.value)}
                        aria-pressed={active}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all duration-150 ${
                          active
                            ? 'border-[#1F4E79] bg-[#1F4E79]/5 text-[#1F4E79] ring-1 ring-[#1F4E79]/20'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-[#1F4E79]/40 hover:bg-gray-50'
                        }`}
                        data-testid={`cs-solution-${i}`}
                      >
                        <Icon size={16} strokeWidth={1.75} />
                        {s.value}
                      </button>
                    );
                  })}
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-[#1F4E79] text-white font-bold rounded-lg hover:bg-[#163a5f] hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="cs-submit"
              >
                {submitting ? 'Sending…' : 'Request A Call'}</button>

              <p className="text-xs text-gray-500 mt-4 flex items-center gap-1.5">
                <CheckCircle2 size={12} className="text-emerald-600" />
                Your information is secure and used only to prepare your sales consultation.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ WHY eCOMM (trust blocks) ═════════════════════╗ */}
      <section className="bg-white py-20 sm:py-24" data-testid="cs-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow>Why eCOMM?</Eyebrow>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-bold text-[#0a1929] leading-tight tracking-tight">A payments partner you can trust.</h2>
            <div className="h-1 w-20 bg-[#ef5b25] rounded-full mx-auto mt-4" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TRUST_BLOCKS.map((t, i) => {
              const Icon = t.icon;
              return (
                <div
                  key={i}
                  className="group rounded-2xl bg-gradient-to-br from-white via-white to-[#f0f5fa] border border-gray-100 shadow-[0_10px_30px_-8px_rgba(31,78,121,0.18)] hover:shadow-[0_18px_38px_-8px_rgba(31,78,121,0.28)] hover:-translate-y-1 hover:border-[#1F4E79]/25 p-6 transition-all duration-300"
                  data-testid={`cs-trust-${i}`}
                >
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#1F4E79]/10 to-[#1F4E79]/5 group-hover:from-[#1F4E79]/15 group-hover:to-[#1F4E79]/10 flex items-center justify-center mb-4 transition-colors duration-200">
                    <Icon size={20} className="text-[#1F4E79]" strokeWidth={1.75} />
                  </div>
                  <h3 className="font-bold text-[#0a1929] text-base leading-snug mb-2">{t.title}</h3>
                  <p className="text-[13.5px] text-gray-600 leading-relaxed">{t.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ WHAT HAPPENS NEXT ═════════════════════╗ */}
      <section className="bg-[#f7f9fc] py-20 sm:py-24" data-testid="cs-next">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Eyebrow>What Happens Next?</Eyebrow>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-bold text-[#0a1929] leading-tight tracking-tight">A simple, transparent process.</h2>
            <div className="h-1 w-20 bg-[#ef5b25] rounded-full mx-auto mt-4" />
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-7 left-[12%] right-[12%] h-px bg-gradient-to-r from-[#1F4E79]/15 via-[#1F4E79]/40 to-[#1F4E79]/15" aria-hidden="true" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {NEXT_STEPS.map((step, i) => (
                <div key={i} className="relative flex flex-col items-center text-center" data-testid={`cs-step-${i}`}>
                  <div className="relative z-10 w-14 h-14 rounded-full bg-gradient-to-br from-[#1F4E79] to-[#0a1929] flex items-center justify-center shadow-lg ring-4 ring-white">
                    <span className="text-white font-bold text-base">{i + 1}</span>
                  </div>
                  <p className="mt-5 text-[14px] text-gray-700 leading-relaxed max-w-[240px]">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ PREFER TO TALK DIRECTLY? ═════════════════════╗ */}
      <section className="py-16 sm:py-20 bg-white" data-testid="cs-direct">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-gradient-to-br from-[#0a1929] via-[#0e2540] to-[#163a5f] p-8 sm:p-14 shadow-xl relative overflow-hidden">
            <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#ef5b25]/15 blur-3xl" aria-hidden="true" />
            <div className="pointer-events-none absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#1F4E79]/25 blur-3xl" aria-hidden="true" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7 text-center lg:text-left">
                <p className="text-[11px] font-bold text-[#ffcba6] uppercase tracking-[0.22em] mb-3">Prefer To Talk Directly?</p>
                <h3 className="text-3xl sm:text-4xl font-bold text-white leading-tight tracking-tight mb-3">Reach out — we're listening.</h3>
                <p className="text-white/85 text-base leading-relaxed max-w-xl mx-auto lg:mx-0">
                  Our team is ready to discuss your payment needs and recommend the right solution for your business.
                </p>
              </div>

              <div className="lg:col-span-5 grid grid-cols-1 gap-3">
                <a
                  href="tel:+353469011288"
                  className="flex items-center gap-3 rounded-xl bg-white/10 backdrop-blur-md border border-white/15 p-4 hover:bg-white/15 transition-all duration-200"
                  data-testid="cs-phone-eu"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#ef5b25]/20 border border-[#ef5b25]/30 flex items-center justify-center">
                    <Phone size={18} className="text-[#ffcba6]" strokeWidth={1.75} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-[#ffcba6] uppercase tracking-[0.18em]">Call EU / UK</p>
                    <p className="text-white text-sm font-bold">+353 (0)46 901 1288 · +44 (0)20 7183 5253</p>
                  </div>
                </a>
                <a
                  href="mailto:sales@ecomm365.com"
                  className="flex items-center gap-3 rounded-xl bg-white/10 backdrop-blur-md border border-white/15 p-4 hover:bg-white/15 transition-all duration-200"
                  data-testid="cs-email"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#ef5b25]/20 border border-[#ef5b25]/30 flex items-center justify-center">
                    <Mail size={18} className="text-[#ffcba6]" strokeWidth={1.75} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-[#ffcba6] uppercase tracking-[0.18em]">Email Us</p>
                    <p className="text-white text-sm font-bold">sales@ecomm365.com</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default ContactSales;
